import { z } from 'zod';
import { getWebAppContext, peekLauncherBotId, peekLauncherInitData } from './maxWebApp';

export const TicketCategoryEnum = z.enum([
  'bug_bot',
  'bot_not_responding',
  'billing',
  'generation',
  'promo',
  'referral',
  'idea',
  'other',
]);
export type TicketCategory = z.infer<typeof TicketCategoryEnum>;

export const TicketStatusEnum = z.enum([
  'open',
  'in_progress',
  'waiting_user',
  'resolved',
  'archived',
]);
export type TicketStatus = z.infer<typeof TicketStatusEnum>;

export const MeSchema = z.object({
  bot_id: z.string(),
  max_user_id: z.string(),
  email: z.string().nullish(),
  consent_at: z.string().nullish(),
});
export type Me = z.infer<typeof MeSchema>;

export const TicketSummarySchema = z.object({
  id: z.string(),
  bot_id: z.string(),
  category: TicketCategoryEnum,
  status: TicketStatusEnum,
  subject: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  closed_at: z.string().nullable(),
});
export type TicketSummary = z.infer<typeof TicketSummarySchema>;

export const TicketMessageSchema = z.object({
  id: z.string(),
  author_kind: z.enum(['user', 'admin', 'system']),
  created_at: z.string(),
  body: z.string(),
  attachments: z.array(
    z.object({ id: z.string(), mime: z.string(), size: z.number(), url: z.string() }),
  ),
});
export type TicketMessage = z.infer<typeof TicketMessageSchema>;

export const TicketWithMessagesSchema = z.object({
  ticket: TicketSummarySchema,
  messages: z.array(TicketMessageSchema),
});

export const UploadResponseSchema = z.object({
  upload_token: z.string(),
  mime: z.string(),
  size: z.number(),
});
export type UploadResponse = z.infer<typeof UploadResponseSchema>;

type ApiErrorPayload = {
  statusCode?: number;
  message?: string;
  error?: string | { code?: string; message?: string };
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Fetch `Headers` only accepts ByteString (ISO-8859-1). Cyrillic in user JSON breaks set(). */
function isLatin1(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) > 255) return false;
  }
  return true;
}

function toHeaderValue(value: string): string {
  return isLatin1(value) ? value : encodeURIComponent(value);
}

function buildHeaders(extra?: HeadersInit): Headers {
  const ctx = getWebAppContext();
  const h = new Headers(extra);
  const botId = ctx.botId || peekLauncherBotId();
  const initData = ctx.initData || peekLauncherInitData();
  if (botId) h.set('X-Bot-Id', botId);
  // Bot display names may be Cyrillic — skip header; auth uses X-Bot-Id.
  if (ctx.botName && isLatin1(ctx.botName)) h.set('X-Bot-Name', ctx.botName);
  if (initData) h.set('X-Max-Init-Data', toHeaderValue(initData));
  if (ctx.devBypass && ctx.devBypassSecret) {
    h.set('X-Dev-Bypass', ctx.devBypassSecret);
  }
  return h;
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; schema?: z.ZodType<T>; formData?: FormData } = {},
): Promise<T> {
  const headers = buildHeaders();
  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData;
  } else if (opts.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(path, { method, headers, body, credentials: 'include' });
  if (!res.ok) {
    let payload: ApiErrorPayload | null = null;
    try {
      payload = (await res.json()) as ApiErrorPayload;
    } catch {
      /* ignore */
    }
    // Fastify:@fastify/sensible → { statusCode, error, message }; our API → { error: { code, message } }
    const nested =
      payload?.error && typeof payload.error === 'object' ? payload.error : null;
    const code =
      nested?.code ??
      (typeof payload?.error === 'string' ? payload.error : undefined) ??
      (typeof payload?.message === 'string' ? payload.message : undefined) ??
      'http_error';
    const message =
      nested?.message ??
      (typeof payload?.message === 'string' ? payload.message : undefined) ??
      `HTTP ${res.status}`;
    throw new ApiError(res.status, code, message);
  }

  if (res.status === 204) return undefined as T;
  const json = (await res.json()) as unknown;
  if (opts.schema) return opts.schema.parse(json);
  return json as T;
}

export const api = {
  getMe: () => request('GET', '/api/me', { schema: MeSchema }),
  updateMe: (data: { email?: string; consent?: boolean }) =>
    request('PATCH', '/api/me', { body: data, schema: MeSchema }),

  listTickets: (status: 'active' | 'archived') =>
    request(
      'GET',
      `/api/tickets?status=${status}`,
      { schema: z.object({ tickets: z.array(TicketSummarySchema) }) },
    ),

  getTicket: (id: string) =>
    request('GET', `/api/tickets/${id}`, { schema: TicketWithMessagesSchema }),

  createTicket: (data: {
    category: TicketCategory;
    body: string;
    attachment_tokens: string[];
  }) =>
    request(
      'POST',
      '/api/tickets',
      { body: data, schema: z.object({ ticket: TicketSummarySchema }) },
    ),

  addMessage: (
    ticketId: string,
    data: { body: string; attachment_tokens: string[] },
  ) =>
    request(
      'POST',
      `/api/tickets/${ticketId}/messages`,
      {
        body: data,
        schema: z.object({
          message: z.object({ id: z.string(), created_at: z.string() }),
        }),
      },
    ),

  uploadFile: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request('POST', '/api/uploads', {
      formData: fd,
      schema: UploadResponseSchema,
    });
  },
};

export const CATEGORY_LABELS: Record<TicketCategory, string> = {
  bug_bot: 'Баг в боте',
  bot_not_responding: 'Бот не отвечает',
  billing: 'Подписка и оплата',
  generation: 'Проблема с генерацией',
  promo: 'Не выдался бонус',
  referral: 'Реферальная программа',
  idea: 'Предложить идею',
  other: 'Другое',
};

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Открыт',
  in_progress: 'В работе',
  waiting_user: 'Ждём вас',
  resolved: 'Закрыт',
  archived: 'В архиве',
};
